import React, { useState, useEffect, useRef } from 'react';
import { CheckCircleIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from './SignaturePad';
import OfferLetterSigning from './OfferLetterSigning';

// Onboarding step: read + sign every active company waiver (auto-filled with the
// new hire's name/position/date; signed copy is filed to their Drive folder).
const OnboardingWaiverStep = ({ onDone }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const [waivers, setWaivers] = useState(null); // null = loading
  const [meta, setMeta] = useState(null);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    fetch(`${backendUrl}/api/announcements/pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(data => {
        const w = (Array.isArray(data) ? data : []).filter(a => a.type === 'waiver');
        setWaivers(w);
        if (w.length === 0) onDone();
      })
      .catch(() => { setWaivers([]); onDone(); });
  }, []);

  const current = waivers && waivers[0];

  useEffect(() => {
    setMeta(null); setScrolledEnd(false); setAgreed(false); setHasInk(false);
    sigRef.current?.clear();
    if (current) {
      if (!current.require_scroll) setScrolledEnd(true);
      fetch(`${backendUrl}/api/waivers/templates/${current.waiver_template_id}/meta`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setMeta).catch(() => setMeta({ page_count: 1 }));
    }
  }, [current?.id]);

  if (waivers === null) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>;
  }
  if (!current) return null;

  const pageCount = meta?.page_count || 0;
  const canSign = (!current.require_scroll || scrolledEnd) && (!current.require_agree || agreed);

  const onDocScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) setScrolledEnd(true);
  };

  const sign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${backendUrl}/api/waivers/${current.id}/sign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_data: sigRef.current.toDataURL('image/png') }),
      });
      if (!r.ok) throw new Error('sign failed');
      const rest = waivers.slice(1);
      setWaivers(rest);
      if (rest.length === 0) onDone();
    } catch (e) {
      alert('Could not record your signature. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="onboarding-waiver-step">
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800">
        {waivers.length > 1 ? `${waivers.length} waivers to sign — ` : ''}Your name, position and date are filled in automatically. The signed copy is filed in your employee records.
      </div>
      <h3 className="text-lg font-bold text-gray-900" data-testid="onboarding-waiver-title">{current.title}</h3>
      {current.message && <p className="text-sm text-gray-600 whitespace-pre-wrap">{current.message}</p>}

      <div
        data-testid="onboarding-waiver-doc-scroll"
        onScroll={onDocScroll}
        className="bg-gray-100 rounded-lg border border-gray-300 overflow-y-auto"
        style={{ maxHeight: '48vh' }}
      >
        {pageCount === 0 ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
        ) : (
          Array.from({ length: pageCount }).map((_, i) => (
            <img
              key={i}
              src={`${backendUrl}/api/waivers/templates/${current.waiver_template_id}/page/${i}?token=${encodeURIComponent(token)}&autofill=1`}
              alt={`Waiver page ${i + 1}`}
              className="w-full block"
              loading="eager"
            />
          ))
        )}
      </div>
      {current.require_scroll && !scrolledEnd && (
        <p className="text-center text-xs text-amber-600" data-testid="onboarding-waiver-scroll-hint">↓ Scroll to the end of the document to continue</p>
      )}

      {current.require_agree && (
        <label className={`flex items-start gap-3 text-sm ${scrolledEnd ? 'text-gray-800 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}>
          <input
            type="checkbox"
            data-testid="onboarding-waiver-agree"
            disabled={!scrolledEnd}
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded shrink-0"
          />
          <span>I have read and understand this waiver, and I agree to be bound by its terms.</span>
        </label>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sign below with your finger or mouse *</label>
        <div className={`relative border-2 border-gray-300 rounded-lg bg-white overflow-hidden ${canSign ? '' : 'opacity-50'}`} data-testid="onboarding-waiver-sign-canvas">
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            backgroundColor="white"
            onEnd={() => setHasInk(true)}
            canvasProps={{ className: 'w-full', style: { width: '100%', height: '160px', touchAction: 'none' } }}
          />
          {!canSign && <div className="absolute inset-0 bg-white/40 cursor-not-allowed" title="Read the document and tick the box to sign" />}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="onboarding-waiver-clear-btn"
          onClick={() => { sigRef.current?.clear(); setHasInk(false); }}
          disabled={submitting}
          className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
        >
          Clear
        </button>
        <button
          type="button"
          data-testid="onboarding-waiver-sign-btn"
          onClick={sign}
          disabled={!hasInk || !canSign || submitting}
          className="flex-1 px-4 py-2 text-sm font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving…' : 'Sign & Accept Waiver'}
        </button>
      </div>
    </div>
  );
};

// Application Info Form Component
const ApplicationInfoForm = ({ onSubmit, defaultName = '', defaultEmail = '', onboardingData }) => {
  const appInfo = onboardingData?.application_info_data || {};
  const _nameParts = (defaultName || '').trim().split(/\s+/).filter(Boolean);
  const [formData, setFormData] = useState({
    firstName: appInfo.firstName || _nameParts[0] || '',
    middle: appInfo.middle || (_nameParts.length > 2 ? _nameParts.slice(1, -1).join(' ') : ''),
    last: appInfo.last || (_nameParts.length > 1 ? _nameParts[_nameParts.length - 1] : ''),
    address: '',
    apt: '',
    city: '',
    province: 'Ontario',
    postalCode: '',
    homeNumber: '',
    mobileNumber: '',
    dateOfBirth: '',
    socialInsurance: '',
    healthCardExpiry: '',
    lastCompletedGrade: '',
    emergencyContact: '',
    emergencyRelation: '',
    emergencyPhone: '',
    email: appInfo.email || defaultEmail || '',
    belongsToUnion: false,
    unionLocal: '',
    tradeMillwright: false,
    tradePipefitting: false,
    journeyman: false,
    allergies: '',
    needsEpiPen: false,
    physicalLimitations: false,
    limitationsHearing: false,
    limitationsSight: false,
    limitationsClimbing: false,
    limitationsLifting: false,
    limitationsOther: false,
    limitationsExplain: '',
    signature: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
          <input
            type="text"
            required
            value={formData.firstName}
            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Middle</label>
          <input
            type="text"
            value={formData.middle}
            onChange={(e) => setFormData({...formData, middle: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
          <input
            type="text"
            required
            value={formData.last}
            onChange={(e) => setFormData({...formData, last: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
          <input
            type="text"
            required
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Apt/Unit</label>
          <input
            type="text"
            value={formData.apt}
            onChange={(e) => setFormData({...formData, apt: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
          <input
            type="text"
            required
            value={formData.city}
            onChange={(e) => setFormData({...formData, city: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Province *</label>
          <select
            required
            value={formData.province}
            onChange={(e) => setFormData({...formData, province: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Ontario">Ontario</option>
            <option value="Quebec">Quebec</option>
            <option value="British Columbia">British Columbia</option>
            <option value="Alberta">Alberta</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
          <input
            type="text"
            required
            value={formData.postalCode}
            onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
            placeholder="A1A 1A1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
          <input
            type="tel"
            required
            value={formData.mobileNumber}
            onChange={(e) => setFormData({...formData, mobileNumber: e.target.value})}
            placeholder="519-555-1234"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
          <input
            type="date"
            required
            value={formData.dateOfBirth}
            onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Social Insurance # *</label>
          <input
            type="text"
            required
            maxLength={11}
            value={formData.socialInsurance}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
              let formatted = digits;
              if (digits.length > 6) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
              else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
              setFormData({...formData, socialInsurance: formatted});
            }}
            placeholder="123-456-789"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Health Card Expiry</label>
          <input
            type="date"
            value={formData.healthCardExpiry}
            onChange={(e) => setFormData({...formData, healthCardExpiry: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Emergency Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.emergencyContact}
              onChange={(e) => setFormData({...formData, emergencyContact: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Relation *</label>
            <input
              type="text"
              required
              value={formData.emergencyRelation}
              onChange={(e) => setFormData({...formData, emergencyRelation: e.target.value})}
              placeholder="Spouse, Parent, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <input
              type="tel"
              required
              value={formData.emergencyPhone}
              onChange={(e) => setFormData({...formData, emergencyPhone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Trade & Union Information</h3>
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.tradeMillwright}
                onChange={(e) => setFormData({...formData, tradeMillwright: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Millwright</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.tradePipefitting}
                onChange={(e) => setFormData({...formData, tradePipefitting: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Pipefitting</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.journeyman}
                onChange={(e) => setFormData({...formData, journeyman: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Journeyman</span>
            </label>
          </div>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.belongsToUnion}
                onChange={(e) => setFormData({...formData, belongsToUnion: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Do you presently belong to a union?</span>
            </label>
            {formData.belongsToUnion && (
              <div className="ml-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Union Local #</label>
                <input
                  type="text"
                  value={formData.unionLocal}
                  onChange={(e) => setFormData({...formData, unionLocal: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter union local number"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Medical Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
            <textarea
              value={formData.allergies}
              onChange={(e) => setFormData({...formData, allergies: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="List any allergies..."
            />
          </div>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.needsEpiPen}
              onChange={(e) => setFormData({...formData, needsEpiPen: e.target.checked})}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">Do you require an EpiPen?</span>
          </label>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Physical Limitations</h3>
        <label className="flex items-center mb-3">
          <input
            type="checkbox"
            checked={formData.physicalLimitations}
            onChange={(e) => setFormData({...formData, physicalLimitations: e.target.checked})}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">Do you have any physical limitations?</span>
        </label>
        {formData.physicalLimitations && (
          <div className="ml-6 space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.limitationsHearing}
                onChange={(e) => setFormData({...formData, limitationsHearing: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Hearing</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.limitationsSight}
                onChange={(e) => setFormData({...formData, limitationsSight: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Sight</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.limitationsClimbing}
                onChange={(e) => setFormData({...formData, limitationsClimbing: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Climbing</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.limitationsLifting}
                onChange={(e) => setFormData({...formData, limitationsLifting: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Lifting</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.limitationsOther}
                onChange={(e) => setFormData({...formData, limitationsOther: e.target.checked})}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Other</span>
            </label>
            {formData.limitationsOther && (
              <textarea
                value={formData.limitationsExplain}
                onChange={(e) => setFormData({...formData, limitationsExplain: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Please explain..."
              />
            )}
          </div>
        )}
      </div>

      <SignaturePad 
        label="Employee Signature"
        onSave={(signatureData) => setFormData({...formData, signature: signatureData})}
      />

      <button
        type="submit"
        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
      >
        Save & Continue
      </button>
    </form>
  );
};

// Direct Deposit Form Component
const DirectDepositForm = ({ onSubmit, onboardingData }) => {
  const appInfo = onboardingData?.application_info_data || {};
  const [mode, setMode] = useState('form'); // 'form' | 'skip'
  const [formData, setFormData] = useState({
    employeeName: appInfo.firstName ? `${appInfo.firstName} ${appInfo.last || ''}`.trim() : '',
    address: appInfo.address || '',
    city: appInfo.city || '',
    province: appInfo.province || 'Ontario',
    postalCode: appInfo.postalCode || '',
    telephone: appInfo.mobileNumber || appInfo.homeNumber || '',
    bankName: '',
    bankAddress: '',
    bankCity: '',
    bankProvince: 'Ontario',
    bankPostalCode: '',
    bankTelephone: '',
    transitNumber: '',
    institutionNumber: '',
    accountNumber: '',
    accountType: 'chequing',
    signature: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleSkip = () => {
    onSubmit({ skipped: true, note: 'Will upload void cheque from phone' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Option:</strong> You can fill out the form below OR skip this step and upload a void cheque photo later from your phone.
        </p>
        <div className="flex gap-3 mt-3">
          <button type="button" onClick={() => setMode('form')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${mode === 'form' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
            Fill Out Form
          </button>
          <button type="button" onClick={() => setMode('skip')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${mode === 'skip' ? 'bg-amber-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
            Skip — Upload Void Cheque Later
          </button>
        </div>
      </div>

      {mode === 'skip' ? (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">You can upload a void cheque photo after onboarding from your phone.</p>
          <button type="button" onClick={handleSkip}
            className="px-8 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold">
            Skip & Continue
          </button>
        </div>
      ) : (
        <>
      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold mb-4">Employee Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              required
              value={formData.employeeName}
              onChange={(e) => setFormData({...formData, employeeName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
            <input
              type="text"
              required
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
            <input
              type="text"
              required
              value={formData.postalCode}
              onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold mb-4">Bank Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
            <input
              type="text"
              required
              value={formData.bankName}
              onChange={(e) => setFormData({...formData, bankName: e.target.value})}
              placeholder="e.g., TD Bank, RBC, Scotia"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Address</label>
            <input
              type="text"
              value={formData.bankAddress}
              onChange={(e) => setFormData({...formData, bankAddress: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank City</label>
            <input
              type="text"
              value={formData.bankCity}
              onChange={(e) => setFormData({...formData, bankCity: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Postal Code</label>
            <input
              type="text"
              value={formData.bankPostalCode}
              onChange={(e) => setFormData({...formData, bankPostalCode: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Phone</label>
            <input
              type="tel"
              value={formData.bankTelephone}
              onChange={(e) => setFormData({...formData, bankTelephone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee Phone</label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => setFormData({...formData, telephone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transit Number (5 digits) *</label>
            <input
              type="text"
              required
              pattern="[0-9]{5}"
              value={formData.transitNumber}
              onChange={(e) => setFormData({...formData, transitNumber: e.target.value})}
              placeholder="12345"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institution Number (3 digits) *</label>
            <input
              type="text"
              required
              pattern="[0-9]{3}"
              value={formData.institutionNumber}
              onChange={(e) => setFormData({...formData, institutionNumber: e.target.value})}
              placeholder="004"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
            <input
              type="text"
              required
              value={formData.accountNumber}
              onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label>
            <select
              required
              value={formData.accountType}
              onChange={(e) => setFormData({...formData, accountType: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="chequing">Chequing</option>
              <option value="savings">Savings</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> Please attach a voided cheque to verify your banking information. You can upload it in the next step.
        </p>
      </div>

      <SignaturePad 
        label="Authorized Signature"
        onSave={(signatureData) => setFormData({...formData, signature: signatureData})}
      />

      <button
        type="submit"
        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
      >
        Save & Continue
      </button>
        </>
      )}
    </form>
  );
};
const CertCheckForm = ({ onSubmit, onboardingData }) => {
  const appInfo = onboardingData?.application_info_data || {};
  const autoName = appInfo.firstName ? `${appInfo.firstName} ${appInfo.last || ''}`.trim() : '';
  const certs = [
    'WHMIS', 'Confined Space', 'Working at Heights', 'Overhead Crane', 
    'Mobile Crane', 'Forklift', 'Boom Lift', 'Scissor Lift', 
    'First Aid', 'CPR', 'Fall Arrest', 'Lock Out Tag Out'
  ];

  const [formData, setFormData] = useState({
    name: autoName,
    certs: {},
    awareOfResponsibility: false,
    signature: '',
    date: new Date().toISOString().split('T')[0]
  });

  const toggleCert = (cert) => {
    setFormData({
      ...formData,
      certs: {
        ...formData.certs,
        [cert]: {
          has: !formData.certs[cert]?.has,
          expiry: formData.certs[cert]?.expiry || ''
        }
      }
    });
  };

  const updateExpiry = (cert, expiry) => {
    setFormData({
      ...formData,
      certs: {
        ...formData.certs,
        [cert]: {
          ...formData.certs[cert],
          expiry
        }
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.awareOfResponsibility) {
      alert('Please confirm you are aware of your responsibilities under the Occupational Health & Safety Act to continue.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Certifications</h3>
        <p className="text-sm text-gray-600 mb-4">Check all certifications you have and provide expiry dates</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certs.map(cert => (
            <div key={cert} className="border rounded-lg p-4">
              <label className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={formData.certs[cert]?.has || false}
                  onChange={() => toggleCert(cert)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">{cert}</span>
              </label>
              {formData.certs[cert]?.has && (
                <input
                  type="date"
                  value={formData.certs[cert]?.expiry || ''}
                  onChange={(e) => updateExpiry(cert, e.target.value)}
                  placeholder="Expiry Date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* OHS Awareness */}
      <div className="border-t pt-4">
        <label className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            required
            data-testid="ohsa-awareness-checkbox"
            checked={formData.awareOfResponsibility}
            onChange={(e) => setFormData({...formData, awareOfResponsibility: e.target.checked})}
            className="h-5 w-5 text-blue-600 border-gray-300 rounded mt-0.5"
          />
          <span className="text-sm text-gray-700">I am aware of my responsibilities under the <strong>Occupational Health & Safety Act</strong> and agree to comply with all company safety policies and procedures. <span className="text-red-600">*</span></span>
        </label>
      </div>

      <SignaturePad 
        label="Employee Signature"
        onSave={(signatureData) => setFormData({...formData, signature: signatureData})}
      />

      <button
        type="submit"
        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
      >
        Save & Continue
      </button>
    </form>
  );
};

// Tax Forms Component (TD1 Federal and Ontario)
const TaxFormsForm = ({ onSubmit, onboardingData }) => {
  const appInfo = onboardingData?.application_info_data || {};
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const [formData, setFormData] = useState({
    firstName: appInfo.firstName || '',
    lastName: appInfo.last || '',
    signature: '',
    date: new Date().toISOString().split('T')[0],
    totalFederalAmount: '16452',
    totalOntarioAmount: '12989',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>TD1 Federal & Ontario Tax Forms</strong><br/>
          Review the CRA TD1 form below. Most employees only need the basic personal amount (pre-filled).
          If you have additional tax credits, note them in the fields below.
        </p>
      </div>

      <div data-testid="tax-forms-disclaimer" className="bg-amber-50 border border-amber-300 rounded-lg p-4">
        <p className="text-sm text-amber-900">
          <strong>Please Note:</strong> Additional tax deductions cannot be adjusted through the app.
          Requests for additional tax withholdings require official government tax forms.
          Contact the main office to receive the necessary forms in person or by email.
        </p>
      </div>

      {/* Embedded CRA TD1 PDFs - 2026 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600 font-medium">TD1 Federal — 2026 Personal Tax Credits Return</span>
          <a href={`${BACKEND}/api/onboarding/form/td1-federal-2026.pdf?token=${token}`} target="_blank" rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800">Open in new tab</a>
        </div>
        <iframe
          src={`${BACKEND}/api/onboarding/form/td1-federal-2026.pdf?token=${token}`}
          title="TD1 Federal 2026"
          className="w-full border-0"
          style={{ height: '400px' }}
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600 font-medium">TD1-ON Ontario — 2026 Provincial Tax Credits</span>
          <a href={`${BACKEND}/api/onboarding/form/td1-ontario-2026.pdf?token=${token}`} target="_blank" rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800">Open in new tab</a>
        </div>
        <iframe
          src={`${BACKEND}/api/onboarding/form/td1-ontario-2026.pdf?token=${token}`}
          title="TD1 Ontario 2026"
          className="w-full border-0"
          style={{ height: '400px' }}
        />
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold mb-4">Your Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input type="text" required value={formData.firstName}
              onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
            <input type="text" required value={formData.lastName}
              onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold mb-4">Tax Credit Amounts</h3>
        <p className="text-sm text-gray-600 mb-3">If you only claim the basic personal amount, leave as-is. Otherwise update to match your TD1.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 p-3 rounded">
            <label className="block text-sm font-medium text-gray-700 mb-1">Federal Claim Amount *</label>
            <input type="text" required value={formData.totalFederalAmount}
              onChange={(e) => setFormData({...formData, totalFederalAmount: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">2026 basic personal: $16,452</p>
          </div>
          <div className="bg-gray-50 p-3 rounded">
            <label className="block text-sm font-medium text-gray-700 mb-1">Ontario Claim Amount *</label>
            <input type="text" required value={formData.totalOntarioAmount}
              onChange={(e) => setFormData({...formData, totalOntarioAmount: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">2026 Ontario basic: $12,989</p>
          </div>
        </div>
      </div>

      <SignaturePad label="Employee Signature"
        onSave={(signatureData) => setFormData({...formData, signature: signatureData})} />

      <button type="submit" className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
        Save & Continue
      </button>
    </form>
  );
};

// Acknowledgement Form Component
const AcknowledgementForm = ({ onSubmit, onboardingData }) => {
  const appInfo = onboardingData?.application_info_data || {};
  const [formData, setFormData] = useState({
    employeeName: appInfo.firstName ? `${appInfo.firstName} ${appInfo.last || ''}`.trim() : '',
    signature: '',
    date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Employee Acknowledgement</strong><br/>
          By signing below, you acknowledge that you have received and understand the employee handbook and company policies.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
        <input
          type="text"
          required
          value={formData.employeeName}
          onChange={(e) => setFormData({...formData, employeeName: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Your full legal name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({...formData, date: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <SignaturePad 
        label="Your Signature"
        onSave={(signatureData) => setFormData({...formData, signature: signatureData})}
      />

      <button
        type="submit"
        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
      >
        Complete Acknowledgement
      </button>
    </form>
  );
};

// Insurance Personal Consent Form Component
const InsuranceConsentForm = ({ onSubmit, onboardingData, defaultName = '' }) => {
  const appInfo = onboardingData?.application_info_data || {};
  const autoName = appInfo.firstName ? `${appInfo.firstName} ${appInfo.last || ''}`.trim() : (defaultName || '');
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const [formData, setFormData] = useState({
    name: autoName,
    signature: '',
    date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.signature) {
      alert('Please add your signature before continuing.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="insurance-consent-form">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Personal Consent for Insurance</strong><br/>
          Please review the consent form below, confirm your name, and sign. Your name and today's date are filled in automatically.
        </p>
      </div>

      {/* Embedded consent PDF */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600 font-medium">Insurance Personal Consent</span>
          <a href={`${BACKEND}/api/onboarding/form/insurance-consent.pdf?token=${token}`} target="_blank" rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800">Open in new tab</a>
        </div>
        <iframe
          src={`${BACKEND}/api/onboarding/form/insurance-consent.pdf?token=${token}`}
          title="Insurance Personal Consent"
          className="w-full border-0"
          style={{ height: '500px' }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
        <input
          type="text"
          required
          data-testid="insurance-consent-name"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Your full legal name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
        <input
          type="date"
          readOnly
          value={formData.date}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
        />
        <p className="text-xs text-gray-500 mt-1">Auto-filled with today's date.</p>
      </div>

      <SignaturePad label="Signature"
        onSave={(signatureData) => setFormData({...formData, signature: signatureData})} />

      <button type="submit" data-testid="insurance-consent-submit"
        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
        Save & Complete Onboarding
      </button>
    </form>
  );
};

const OnboardingWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const steps = [
    { num: 1, name: 'Offer Letter', description: 'Review and sign your offer letter' },
    { num: 2, name: 'Application Info', description: 'Personal information', pdf: 'applicationinfo.pdf' },
    { num: 3, name: 'Cert Check', description: 'Certifications & licenses', pdf: 'certcheck.pdf' },
    { num: 4, name: 'Direct Deposit', description: 'Banking information', pdf: 'directdeposit.pdf' },
    { num: 5, name: 'Tax Forms', description: 'Federal & Ontario', pdf: 'taxforms.pdf' },
    { num: 6, name: 'Acknowledgement', description: 'Sign acknowledgement', pdf: 'acknowledge.pdf' },
    { num: 7, name: 'Insurance Consent', description: 'Personal consent for insurance', pdf: 'insurance-consent.pdf' },
    { num: 8, name: 'Waivers & Policies', description: 'Read and sign required company waivers' }
  ];

  const finishedRef = useRef(false);
  const finishOnboarding = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    alert('🎉 Onboarding Complete!\n\nAll forms have been successfully submitted and uploaded to Google Drive. You will now be redirected to watch the safety video.');
    window.location.replace('/onboarding-video');
  };

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  // Handle legacy step numbers (reset to last step if out of bounds)
  useEffect(() => {
    if (currentStep > steps.length) {
      setCurrentStep(steps.length);
    }
  }, [currentStep, steps.length]);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/onboarding/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setOnboardingData(data.onboarding_data || {});
      setUserName(data.full_name || '');
      setUserEmail(data.email || '');
      const step = data.onboarding_data?.current_step || 1;
      // Cap at max steps if legacy data has higher step number
      setCurrentStep(Math.min(step, steps.length));
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const uploadFilledPDF = async (file, stepName) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('step', stepName);

      const response = await fetch(`${backendUrl}/api/onboarding/upload-filled-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        alert('PDF uploaded successfully!');
        fetchOnboardingStatus();
      } else {
        alert('Failed to upload PDF');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentStepInfo = steps[currentStep - 1] || steps[steps.length - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Onboarding</h1>
            <p className="text-sm text-gray-600">Step {currentStep} of {steps.length}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative">
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded-full bg-gray-200">
            <div
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-500"
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-600 mb-8">
            {steps.map((step) => (
              <div key={step.num} className="text-center">
                <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                  step.num < currentStep ? 'bg-green-500 text-white' :
                  step.num === currentStep ? 'bg-blue-500 text-white' :
                  'bg-gray-300 text-gray-600'
                }`}>
                  {step.num < currentStep ? '✓' : step.num}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentStepInfo.name}</h2>
          <p className="text-gray-600 mb-6">{currentStepInfo.description}</p>

          {/* Step Content */}
          {currentStep === 1 && (
            <>
              {onboardingData?.offer_letter_url ? (
                <OfferLetterSigning
                  offerLetterUrl={onboardingData.offer_letter_url}
                  onComplete={async (signatureData) => {
                    try {
                      setLoading(true);
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${backendUrl}/api/onboarding/sign-offer-letter`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(signatureData)
                      });

                      if (response.ok) {
                        alert('✅ Offer letter signed! Proceeding to onboarding forms.');
                        await fetchOnboardingStatus();
                        setCurrentStep(2);
                      } else {
                        throw new Error('Failed to sign offer letter');
                      }
                    } catch (error) {
                      throw error;
                    } finally {
                      setLoading(false);
                    }
                  }}
                />
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Waiting for HR</h3>
                  <p className="text-gray-600">Chelsea Barnes will upload your offer letter shortly. You'll receive a notification when it's ready.</p>
                </div>
              )}
            </>
          )}

          {currentStep === 2 && (
            <ApplicationInfoForm defaultName={userName} defaultEmail={userEmail} onboardingData={onboardingData} onSubmit={async (formData) => {
              try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`${backendUrl}/api/onboarding/submit-application-info`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(formData)
                });

                if (response.ok) {
                  const result = await response.json();
                  alert('✅ Application info saved and PDF uploaded to Google Drive!');
                  await fetchOnboardingStatus();
                  setCurrentStep(3);
                } else {
                  const error = await response.json();
                  alert(`Failed to save: ${error.detail || 'Unknown error'}`);
                }
              } catch (error) {
                console.error('Submission error:', error);
                alert('Failed to submit form. Please try again.');
              } finally {
                setLoading(false);
              }
            }} />
          )}

          {currentStep === 3 && (
            <CertCheckForm onboardingData={onboardingData} onSubmit={async (formData) => {
              try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`${backendUrl}/api/onboarding/submit-cert-check`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(formData)
                });

                if (response.ok) {
                  const result = await response.json();
                  alert('✅ Certifications saved and PDF uploaded to Google Drive!');
                  await fetchOnboardingStatus();
                  setCurrentStep(4);
                } else {
                  const error = await response.json();
                  alert(`Failed to save: ${error.detail || 'Unknown error'}`);
                }
              } catch (error) {
                console.error('Submission error:', error);
                alert('Failed to submit form. Please try again.');
              } finally {
                setLoading(false);
              }
            }} />
          )}

          {currentStep === 4 && (
            <DirectDepositForm onboardingData={onboardingData} onSubmit={async (formData) => {
              try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`${backendUrl}/api/onboarding/submit-direct-deposit`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(formData)
                });

                if (response.ok) {
                  const result = await response.json();
                  alert('✅ Direct deposit info saved and PDF uploaded to Google Drive!');
                  await fetchOnboardingStatus();
                  setCurrentStep(5);
                } else {
                  const error = await response.json();
                  alert(`Failed to save: ${error.detail || 'Unknown error'}`);
                }
              } catch (error) {
                console.error('Submission error:', error);
                alert('Failed to submit form. Please try again.');
              } finally {
                setLoading(false);
              }
            }} />
          )}

          {currentStep === 5 && (
            <TaxFormsForm onboardingData={onboardingData} onSubmit={async (formData) => {
              try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`${backendUrl}/api/onboarding/submit-tax-forms`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(formData)
                });

                if (response.ok) {
                  const result = await response.json();
                  alert('✅ Tax forms saved and PDF uploaded to Google Drive!');
                  await fetchOnboardingStatus();
                  setCurrentStep(6);
                } else {
                  const error = await response.json();
                  alert(`Failed to save: ${error.detail || 'Unknown error'}`);
                }
              } catch (error) {
                console.error('Submission error:', error);
                alert('Failed to submit form. Please try again.');
              } finally {
                setLoading(false);
              }
            }} />
          )}

          {currentStep === 6 && (
            <AcknowledgementForm onboardingData={onboardingData} onSubmit={async (formData) => {
              // Prevent multiple submissions
              if (loading) {
                return;
              }
              
              setLoading(true);
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${backendUrl}/api/onboarding/submit-acknowledgement`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(formData)
                });

                if (response.ok) {
                  const result = await response.json();
                  alert('✅ Acknowledgement saved! One more form to go.');
                  await fetchOnboardingStatus();
                  setCurrentStep(7);
                } else {
                  const error = await response.json();
                  alert(`Failed to save: ${error.detail || 'Unknown error'}`);
                  setLoading(false);
                }
              } catch (error) {
                console.error('Submission error:', error);
                alert('Failed to submit form. Please try again.');
                setLoading(false);
              }
            }} />
          )}

          {currentStep === 7 && (
            <InsuranceConsentForm onboardingData={onboardingData} defaultName={userName} onSubmit={async (formData) => {
              if (loading) return;
              setLoading(true);
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${backendUrl}/api/onboarding/submit-insurance-consent`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(formData)
                });

                if (response.ok) {
                  // Move to the final step: read + sign any active company waivers
                  let hasWaivers = false;
                  try {
                    const pr = await fetch(`${backendUrl}/api/announcements/pending`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const pend = pr.ok ? await pr.json() : [];
                    hasWaivers = (Array.isArray(pend) ? pend : []).some(a => a.type === 'waiver');
                  } catch (e) { /* no waivers reachable — finish normally */ }
                  if (hasWaivers) {
                    setCurrentStep(8);
                    setLoading(false);
                  } else {
                    finishOnboarding();
                  }
                } else {
                  const error = await response.json();
                  alert(`Failed to save: ${error.detail || 'Unknown error'}`);
                  setLoading(false);
                }
              } catch (error) {
                console.error('Submission error:', error);
                alert('Failed to submit form. Please try again.');
                setLoading(false);
              }
            }} />
          )}

          {currentStep === 8 && (
            <OnboardingWaiverStep onDone={finishOnboarding} />
          )}

          {/* Removed dummy steps 4,5,9,10 - Health Card, Driver's License, Safety Video, Selfie */}

          {/* Navigation - Only show for non-form steps */}
          {currentStep === 1 && (
            <div className="mt-8 flex justify-end">
              <p className="text-sm text-gray-600 italic">Forms include their own submission buttons</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
